import React, { useEffect, useRef, useState } from "react";

interface AudioVisualizerProps {
  audioLevel: number;
  isActive: boolean;
  type: "input" | "output";
  className?: string;
}

export const AudioVisualizer: React.FC<AudioVisualizerProps> = ({
  audioLevel,
  isActive,
  type,
  className = "",
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const [bars] = useState(() =>
    Array.from({ length: 8 }, () => Math.random() * 0.5 + 0.1)
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const barWidth = canvas.width / bars.length;
      const centerY = canvas.height / 2;

      bars.forEach((bar, index) => {
        const x = index * barWidth;
        const baseHeight = isActive ? audioLevel * 20 + 4 : 2;
        const height =
          baseHeight *
          (bar + (isActive ? Math.sin(Date.now() * 0.01 + index) * 0.3 : 0));

        // Color based on type and state
        const color =
          type === "input"
            ? isActive
              ? "#ef4444"
              : "#9ca3af"
            : isActive
            ? "#3b82f6"
            : "#9ca3af";

        ctx.fillStyle = color;
        ctx.fillRect(x + 1, centerY - height / 2, barWidth - 2, height);
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [audioLevel, isActive, type, bars]);

  return (
    <canvas
      ref={canvasRef}
      width={80}
      height={24}
      className={`rounded-sm ${className}`}
      aria-label={`${type} audio visualization`}
    />
  );
};
